import { useState, useEffect, useRef } from 'react';

/**
 * useSpamProtection Hook
 * Provides honeypot field, timestamp tracking, and form state for spam protection
 */
export function useSpamProtection() {
  const [formTimestamp] = useState(() => Date.now() / 1000); // Unix timestamp in seconds
  const [honeypot, setHoneypot] = useState('');
  
  return {
    // Include these in your form submission
    spamFields: {
      honeypot,
      form_timestamp: formTimestamp,
    },
    // Render this hidden honeypot field in your form
    HoneypotField: () => (
      <div 
        style={{ 
          position: 'absolute', 
          left: '-9999px', 
          opacity: 0, 
          height: 0, 
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
        aria-hidden="true"
        tabIndex={-1}
      >
        <label htmlFor="website_url">Website (leave empty)</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          autoComplete="off"
          tabIndex={-1}
        />
      </div>
    ),
  };
}

/**
 * SpamProtectedForm Wrapper Component
 * Wraps any form with spam protection features
 */
export function SpamProtectedForm({ 
  children, 
  onSubmit, 
  className = '',
  ...props 
}) {
  const { spamFields, HoneypotField } = useSpamProtection();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    
    try {
      // Get form data
      const formData = new FormData(e.target);
      const data = Object.fromEntries(formData.entries());
      
      // Add spam protection fields
      const protectedData = {
        ...data,
        ...spamFields,
      };
      
      await onSubmit(protectedData);
    } catch (err) {
      if (err.message?.includes('429') || err.message?.includes('Too many')) {
        setError('Too many submissions. Please wait a while before trying again.');
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className={className} {...props}>
      <HoneypotField />
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
          {error}
        </div>
      )}
      {typeof children === 'function' 
        ? children({ isSubmitting, spamFields }) 
        : children
      }
    </form>
  );
}

/**
 * Helper to add spam fields to existing form submission
 */
export function addSpamProtection(formData, spamFields) {
  return {
    ...formData,
    ...spamFields,
  };
}

export default useSpamProtection;
